import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef } from "react";

const panels = [
  {
    title: "The Problem",
    icon: "/images/broken-heart.png",
    tag: "😤 We've all been there",
    deco: "??!",
    rotate: -2,
    cardStyle: {
      background: "linear-gradient(145deg, #d63384 0%, #a0145c 100%)",
      border: "4px solid #000000",
      boxShadow: "0 10px 0 #000000, 0 16px 40px rgba(0,0,0,0.35)",
    },
    hoverShadow: "0 4px 0 #000000, 0 8px 20px rgba(0,0,0,0.3)",
    titleColor: "text-white",
    decoColor: "text-white",
    dividerColor: "bg-white/40",
    tagStyle: "bg-white/20 text-white border border-white/30",
    blobColor: "bg-white/10",
    content: (
      <p className="text-sm lg:text-base text-left font-bold font-comfortaa leading-relaxed text-white/90">
        Breaking up sucks. Whether it's a{" "}
        <span className="bg-white/25 rounded px-1">toxic relationship</span>, a bad
        business partnership, or just needing to cut ties with negativity,
        everyone has an ex they'd rather forget.
        <br /><br />
        Traditional finance doesn't let you turn that anger, that clarity, that drive into anything real.{" "}
        <strong className="text-white">FMEX does.</strong>
      </p>
    ),
  },
  {
    title: "About FMEX",
    icon: "🚀",
    tag: "💪 Turn pain into gains",
    deco: "!!",
    rotate: 1,
    cardStyle: {
      background: "linear-gradient(145deg, #FFE566 0%, #FFD000 100%)",
      border: "4px solid #000000",
      boxShadow: "0 10px 0 #000000, 0 16px 40px rgba(0,0,0,0.25)",
    },
    hoverShadow: "0 4px 0 #000000, 0 8px 20px rgba(0,0,0,0.2)",
    titleColor: "text-amber-900",
    decoColor: "text-black",
    dividerColor: "bg-black/20",
    tagStyle: "bg-black/10 text-amber-900 border border-black/20",
    blobColor: "bg-orange-400/20",
    content: (
      <p className="text-sm lg:text-base text-left font-bold font-comfortaa leading-relaxed text-amber-900">
        FMEX is a{" "}
        <span className="bg-black/10 rounded px-1">community-powered movement</span>{" "}
        disguised as a meme coin, created to transform past trauma into
        forward-moving momentum.
        <br /><br />
        We combine viral meme culture with{" "}
        <strong>legitimate tokenomics</strong> and utility to create a community
        where <em>holding becomes therapeutic.</em>
      </p>
    ),
  },
];

function CardInner({ panel }) {
  return (
    <div className="relative w-full h-full flex flex-col items-start overflow-hidden">
      {/* Background blob */}
      <div
        className={`absolute -bottom-10 -right-10 w-48 h-48 rounded-full blur-3xl pointer-events-none ${panel.blobColor}`}
      />

      {/* Watermark deco text */}
      <div
        className={`absolute top-3 right-4 font-darumadrop text-6xl leading-none pointer-events-none opacity-10 ${panel.decoColor}`}
      >
        {panel.deco}
      </div>

      {/* Icon */}
      <div className="relative z-10 w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 border-2 border-white/30 bg-white/15 shadow-md">
      {panel.icon.startsWith("/") ? (
        <img src={panel.icon} alt="" className="w-8 h-8 object-contain" />
      ) : (
        panel.icon
      )}
    </div>

      {/* Title */}
      <h3 className={`relative z-10 uppercase font-darumadrop text-3xl lg:text-4xl leading-none ${panel.titleColor}`}>
        {panel.title}
      </h3>

      {/* Divider */}
      <div className={`relative z-10 mt-3 mb-4 h-1 w-12 rounded-full ${panel.dividerColor}`} />

      {/* Body */}
      <div className="relative z-10 flex-1">{panel.content}</div>

      {/* Bottom tag */}
      <div className={`relative z-10 mt-6 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest ${panel.tagStyle}`}>
        {panel.tag}
      </div>
    </div>
  );
}

function HoverCard({ panel }) {
  const ref = useRef(null);

  return (
    <motion.div
      ref={ref}
      initial={{ rotate: panel.rotate }}
      whileHover={{ scale: 1.04, rotate: 0 }}
      transition={{ duration: 0.25 }}
      style={{ ...panel.cardStyle, transformOrigin: "center center" }}
      onHoverStart={() => {
        if (ref.current) ref.current.style.boxShadow = panel.hoverShadow;
      }}
      onHoverEnd={() => {
        if (ref.current) ref.current.style.boxShadow = panel.cardStyle.boxShadow;
      }}
      className="rounded-3xl p-10 lg:p-12 cursor-default"
    >
      <CardInner panel={panel} />
    </motion.div>
  );
}

export default function OriginComic() {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => (c - 1 + panels.length) % panels.length);
  const next = () => setCurrent((c) => (c + 1) % panels.length);

  return (
    <section id="origin" className="relative py-16 overflow-hidden">

      {/* Section heading */}
      <div className="text-center mb-10 px-6">
        <h2 className="font-darumadrop uppercase text-4xl lg:text-6xl text-white drop-shadow-[3px_3px_0px_#030303]">
          Why FMEX Exists
        </h2>
        <p className="font-comfortaa font-bold text-black mt-3 text-sm lg:text-base max-w-xl mx-auto leading-relaxed">
          Because the world needed a meme coin with actual purpose, and your ex needed a reason to regret everything.
        </p>
      </div>

      {/* ── MOBILE: card switcher ── */}
      <div className="flex md:hidden flex-col items-center px-6 py-4">
        <div className="relative w-full" style={{ minHeight: "380px" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.3 }}
              className="w-full rounded-3xl p-8"
              style={panels[current].cardStyle}
            >
              <CardInner panel={panels[current]} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-6 mt-6">
          <button
            onClick={prev}
            className="w-12 h-12 rounded-full border-4 border-black bg-white text-black font-black text-xl shadow-md active:scale-95 transition-transform flex items-center justify-center"
          >
            ‹
          </button>
          <span className="font-darumadrop text-sm">
            {current + 1} / {panels.length}
          </span>
          <button
            onClick={next}
            className="w-12 h-12 rounded-full border-4 border-black bg-black text-white font-black text-xl shadow-md active:scale-95 transition-transform flex items-center justify-center"
          >
            ›
          </button>
        </div>
      </div>

      {/* ── DESKTOP: 2 cards side by side ── */}
      <div className="hidden md:grid grid-cols-2 gap-20 px-16 py-6 max-w-7xl mx-auto">
        {panels.map((panel, index) => (
          <HoverCard key={index} panel={panel} />
        ))}
      </div>

    </section>
  );
}